import { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://api.cloudnine.biz.id';

/**
 * Custom hook untuk mengelola character notes (kanban cards)
 * 
 * @param {number|null} characterId - ID karakter
 * @returns {Object} { notes, loading, error, refetch, addCard, addItem, updateNote, deleteNote }
 */
export const useCharacterNotes = (characterId) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Fetch semua notes untuk karakter
     */
    const fetchNotes = useCallback(async () => {
        if (!characterId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/notes.php?character_id=${characterId}`);
            const data = await response.json();

            if (data.success) {
                setNotes(data.data || []);
            } else {
                setError(data.error || 'Failed to fetch notes');
            }
        } catch (err) {
            setError(err.message || 'Network error');
        } finally {
            setLoading(false);
        }
    }, [characterId]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    /**
     * Tambah card baru (parent - hanya judul)
     */
    const addCard = async (title) => {
        try {
            const response = await fetch(`${API_BASE}/notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    character_id: characterId,
                    title,
                }),
            });
            const data = await response.json();
            if (data.success) {
                // Add new card to local state
                setNotes(prev => [...prev, { ...data.data, children: [] }]);
                return data.data;
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            console.error('Error adding card:', err);
            return null;
        }
    };

    /**
     * Tambah item baru ke card (child)
     */
    const addItem = async (parentId, { content, image }) => {
        try {
            const response = await fetch(`${API_BASE}/notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    character_id: characterId,
                    parent_id: parentId,
                    content: content || '',
                    image: image || null,
                }),
            });
            const data = await response.json();
            if (data.success) {
                // Add child to the correct parent card locally
                setNotes(prev => prev.map(card =>
                    card.id === parentId
                        ? { ...card, children: [...(card.children || []), data.data] }
                        : card
                ));
                return data.data;
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            console.error('Error adding item:', err);
            return null;
        }
    };

    /**
     * Update note (bisa card atau item)
     */
    const updateNote = async (id, updates) => {
        try {
            const response = await fetch(`${API_BASE}/notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, ...updates }),
            });
            const data = await response.json();
            if (data.success) {
                setNotes(prev => prev.map(card => {
                    if (card.id === id) {
                        return { ...card, ...data.data, children: card.children };
                    }
                    // Check children
                    return {
                        ...card,
                        children: (card.children || []).map(child =>
                            child.id === id ? { ...child, ...data.data } : child
                        ),
                    };
                }));
                return data.data;
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            console.error('Error updating note:', err);
            return null;
        }
    };

    /**
     * Hapus note (card atau item, cascade)
     */
    const deleteNote = async (id, parentId = null) => {
        try {
            const response = await fetch(`${API_BASE}/notes.php?id=${id}&action=delete`, {
                method: 'POST',
            });
            const data = await response.json();
            if (data.success) {
                if (parentId) {
                    // Hapus child dari parent
                    setNotes(prev => prev.map(card =>
                        card.id === parentId
                            ? { ...card, children: (card.children || []).filter(c => c.id !== id) }
                            : card
                    ));
                } else {
                    // Hapus card
                    setNotes(prev => prev.filter(card => card.id !== id));
                }
                return true;
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            console.error('Error deleting note:', err);
            return false;
        }
    };

    /**
     * Pindahkan item ke card lain (drag & drop)
     */
    const moveItem = async (itemId, fromCardId, toCardId) => {
        if (fromCardId === toCardId) return true;

        try {
            const response = await fetch(`${API_BASE}/notes.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: itemId, parent_id: toCardId }),
            });
            const data = await response.json();
            if (data.success) {
                setNotes(prev => {
                    let movedItem = null;
                    // Remove from source card
                    const updated = prev.map(card => {
                        if (card.id === fromCardId) {
                            const item = (card.children || []).find(c => c.id === itemId);
                            if (item) movedItem = { ...item, parent_id: toCardId };
                            return { ...card, children: (card.children || []).filter(c => c.id !== itemId) };
                        }
                        return card;
                    });
                    // Add to target card
                    if (movedItem) {
                        return updated.map(card =>
                            card.id === toCardId
                                ? { ...card, children: [...(card.children || []), movedItem] }
                                : card
                        );
                    }
                    return updated;
                });
                return true;
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            console.error('Error moving item:', err);
            return false;
        }
    };

    return {
        notes,
        loading,
        error,
        refetch: fetchNotes,
        addCard,
        addItem,
        updateNote,
        deleteNote,
        moveItem,
    };
};
